"use client"

import { useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { LinkedInMedia } from '@/lib/models/linkedin-post';
import { cn } from '@/lib/utils';

// Media del post en la página de detalle: vídeo con controles, o imagen/carrusel.
export default function NewsMedia({ media, cover, label }: { media: LinkedInMedia[]; cover: string | null; label: string }) {
  const [index, setIndex] = useState(0);
  const video = media.find(m => m.type === 'video');
  const images = media.filter(m => m.type === 'image');

  if (video) {
    return (
      <video
        controls
        preload="metadata"
        poster={video.thumbnail ?? cover ?? undefined}
        className="w-full rounded-3xl bg-slate-900 shadow-xl"
      >
        <source src={video.url} type="video/mp4" />
      </video>
    );
  }

  const slides = images.length > 0 ? images.map(m => m.url) : cover ? [cover] : [];
  if (slides.length === 0) return null;

  const go = (i: number) => setIndex((i + slides.length) % slides.length);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-slate-100 shadow-xl">
        <Image
          src={slides[index]}
          unoptimized
          alt={`${label} ${index + 1} / ${slides.length}`}
          fill
          priority
          sizes="(min-width: 1024px) 896px, 100vw"
          className="object-contain"
        />
        {slides.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label="←"
              className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-md transition hover:bg-white hover:text-cyan-700"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label="→"
              className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-md transition hover:bg-white hover:text-cyan-700"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>
      {slides.length > 1 && (
        <div className="flex justify-center gap-1">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              aria-label={`${label} ${i + 1}`}
              aria-current={i === index}
              className="flex h-8 w-8 cursor-pointer items-center justify-center"
            >
              <span className={cn("h-1.5 rounded-full transition-all", i === index ? "w-6 bg-cyan-600" : "w-2 bg-slate-300")} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
